import { motion } from "framer-motion";
import { Chapter, MaskedLines, Fade, EASE } from "./motion";

const CENTER = { x: 600, y: 350 };
const NODES = [
    { label: "STUDENT", sub: "THE TALENT", cx: 600, cy: 70, x: 515, y: 42 },
    { label: "CAMPUS", sub: "THE ECOSYSTEM", cx: 1000, cy: 200, x: 915, y: 172 },
    { label: "AI", sub: "THE ENGINE", cx: 1000, cy: 500, x: 915, y: 472 },
    { label: "INDUSTRY", sub: "THE STANDARD", cx: 200, cy: 500, x: 115, y: 472 },
    { label: "CAREER", sub: "THE OUTCOME", cx: 200, cy: 200, x: 115, y: 172 },
];
const PLUSES = [
    { x: 800, y: 120 },
    { x: 1000, y: 350 },
    { x: 600, y: 500 },
    { x: 200, y: 350 },
    { x: 400, y: 120 },
];

const CONNECTIONS = [
    { a: "STUDENT × AI", b: "Continuous practice and evaluation, on demand." },
    { a: "QRUS × CAMPUS", b: "Installed inside the academic ecosystem." },
    { a: "QRUS × INDUSTRY", b: "Exposure, internships, and live project pipelines." },
    { a: "STUDENT × CAREER", b: "A four-year trajectory, measured at every step." },
];

const Diagram = () => (
    <svg viewBox="0 0 1200 700" className="h-auto w-full min-w-[680px] font-sans" role="img" aria-label="QRUS framework diagram: student, campus, AI, industry and career connected through one core">
        <circle cx={CENTER.x} cy={CENTER.y} r={310} fill="none" stroke="rgba(255,255,255,0.07)" strokeDasharray="3 9" />

        {NODES.map((n, i) => (
            <motion.line
                key={n.label}
                x1={CENTER.x} y1={CENTER.y} x2={n.cx} y2={n.cy}
                stroke="rgba(130,200,229,0.45)" strokeWidth="1" strokeDasharray="6 8"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.3 + i * 0.15 }}
            />
        ))}

        {PLUSES.map((p) => (
            <text key={`${p.x}-${p.y}`} x={p.x} y={p.y} textAnchor="middle" fontSize="22" fill="#6D8196" fillOpacity="0.8">
                +
            </text>
        ))}

        {NODES.map((n, i) => (
            <motion.g
                key={n.label}
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.5 + i * 0.15 }}
            >
                <rect x={n.x} y={n.y} width={170} height={56} fill="#0B1220" stroke="rgba(130,200,229,0.5)" />
                <text x={n.cx} y={n.y + 25} textAnchor="middle" fontSize="14" fontWeight="600" letterSpacing="3.5" fill="#FFFFFF">
                    {n.label}
                </text>
                <text x={n.cx} y={n.y + 42} textAnchor="middle" fontSize="8" letterSpacing="2" fill="#6D8196">
                    {n.sub}
                </text>
                <motion.circle
                    cx={n.x + 170} cy={n.y} r={4} fill="#82C8E5"
                    animate={{ opacity: [0.25, 1, 0.25] }}
                    transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
                />
            </motion.g>
        ))}

        <motion.g
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        >
            <rect x={CENTER.x - 95} y={CENTER.y - 34} width={190} height={68} fill="#0047AB" />
            <rect x={CENTER.x - 101} y={CENTER.y - 40} width={202} height={80} fill="none" stroke="rgba(130,200,229,0.4)" strokeDasharray="4 6" />
            <text x={CENTER.x} y={CENTER.y - 4} textAnchor="middle" fontSize="17" fontWeight="700" letterSpacing="4" fill="#FFFFFF">
                QRUS CORE
            </text>
            <text x={CENTER.x} y={CENTER.y + 17} textAnchor="middle" fontSize="8" letterSpacing="2.5" fill="#82C8E5">
                TALENT OPERATING SYSTEM
            </text>
        </motion.g>

        <text x={20} y={30} fontSize="9" letterSpacing="2" fill="#6D8196">0,0</text>
        <text x={1180} y={685} textAnchor="end" fontSize="9" letterSpacing="2" fill="#6D8196">1200,700</text>
    </svg>
);

export default function Framework() {
    return (
        <section className="blueprint-grid-dark bg-ink py-20 text-white md:py-40" data-testid="framework-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.07" title="The Framework" dark />

                <div className="mt-16 md:mt-24">
                    <MaskedLines
                        stagger={0.13}
                        lineClassName="font-display text-[clamp(2.2rem,7.5vw,5.4rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-white"
                        lines={[
                            <span key="a">Five forces.</span>,
                            <span key="b" className="text-sky">One operating core.</span>,
                        ]}
                    />
                </div>

                <div className="mt-16 grid gap-14 md:mt-24 lg:grid-cols-12 lg:gap-10">
                    <Fade delay={0.2} className="lg:col-span-8">
                        <div className="relative overflow-x-auto border border-white/10 bg-white/[0.02] p-4 md:p-8">
                            <span className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-sky" />
                            <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-sky" />
                            <Diagram />
                            <div className="flex items-center justify-between border-t border-white/10 px-2 pt-4">
                                <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                                    FIG.05 — System Architecture
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.3em] text-sky/60">
                                    NODES: 05 / EDGES: 05
                                </span>
                            </div>
                        </div>
                    </Fade>

                    <div className="flex flex-col justify-center gap-0 lg:col-span-4">
                        {CONNECTIONS.map((c, i) => (
                            <Fade key={c.a} delay={0.2 + i * 0.1} y={16}>
                                <div className="border-t border-white/10 py-7">
                                    <p className="font-display text-sm font-semibold tracking-[0.2em] text-sky">
                                        {c.a}
                                    </p>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate">
                                        {c.b}
                                    </p>
                                </div>
                            </Fade>
                        ))}
                        <div className="border-t border-white/10" />
                    </div>
                </div>
            </div>
        </section>
    );
}
